import SearchConditionRow from "@/components/SearchConditionRow";
import SearchConditionTag, {
  SearchConditionTagType,
} from "@/components/SearchConditionTag";
import { FC, PropsWithChildren, useMemo } from "react";
import useProductSearchCondition from "@/features/product_variation/hooks/useProductSearchCondition";
import useDisclosure from "@/hooks/useDisclosure";
import ModalSearchMaker from "@/components/ModalSearchMaker";
import SearchGroup from "@/components/SearchGroup";
import {
  BrandWithCategoriesResponse,
  MakerWithCategoriesResponse,
  ProductKind,
} from "@/web-client/api";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import { SearchQueryParameterController } from "@/hooks/useSearchQueryParameter";
import SearchProductDropdowns from "@/components/SearchProductDropdowns";
import useCompanyTabs, { CompanyTabState } from "@/hooks/useCompanyTabs";
import Tabs from "@/components/Tabs";
import { ProductSearchHeader } from "@/features/sort";

interface Props {
  sidebar?: JSX.Element;
  showCondition?: boolean;
  headerView?: JSX.Element;
  totalCount: number;
  maker: MakerWithCategoriesResponse | BrandWithCategoriesResponse;
  query: ProductSearchQueryParameter;
  queryController?: SearchQueryParameterController<ProductSearchQueryParameter>;
  kind?: ProductKind;

  isBrand?: boolean;
  tabState: CompanyTabState;
}

const MakerSearchGroup: FC<PropsWithChildren<Props>> = ({
  children,
  maker,
  sidebar,
  headerView,
  totalCount,
  query,
  queryController,
  kind,
  isBrand = false,
  tabState,
}): JSX.Element => {
  const { hasCondition, conditions } = useProductSearchCondition(
    query,
    maker.categories,
  );
  const { openHandler, closeHandler, isOpen } = useDisclosure();

  const tabs = useCompanyTabs(!isBrand, maker, tabState, query);

  const availableSample = useMemo(() => {
    if (isBrand) return true;

    return (maker as MakerWithCategoriesResponse).has_sample_item;
  }, [isBrand, maker]);

  return (
    <SearchGroup
      headerView={headerView}
      hasCondition={hasCondition}
      hiddenBoards={true}
      dropdowns={
        <SearchProductDropdowns
          kind={kind}
          onOpenModal={openHandler}
          categories={maker.categories}
          query={query}
          queryController={queryController}
        />
      }
      modal={
        <ModalSearchMaker
          kind={kind}
          closeHandler={closeHandler}
          query={query}
        />
      }
      modalOpen={isOpen}
      sidebar={sidebar}
      conditions={
        queryController && (
          <SearchConditionRow
            basePath={queryController.basePath}
            visibleClearBtn={hasCondition}
          >
            {conditions.map((condition) => {
              return (
                <SearchConditionTag
                  key={condition.label}
                  name={condition.label}
                  type={SearchConditionTagType.Delete}
                  onClick={() => {
                    queryController.deleteQueryAt(
                      condition.key,
                      condition.value,
                    );

                    // カテゴリが削除された場合は、詳細検索項目も削除
                    if (condition.key === "category_id") {
                      queryController.deleteQueryAt(
                        "search_condition_value_ids",
                        query.search_condition_value_ids,
                      );
                    }

                    queryController.submitQuery();
                  }}
                />
              );
            })}
          </SearchConditionRow>
        )
      }
    >
      <div className="space-y-12 laptop:space-y-20">
        <Tabs tabs={tabs} amount={totalCount} />
        <ProductSearchHeader
          queryController={queryController}
          query={query}
          availableSample={availableSample}
          sortable={false} // 暫定的にメーカー・ブランドページではソートを表示しない
        />

        {children}
      </div>
    </SearchGroup>
  );
};

export default MakerSearchGroup;
